import { Assets, Container, Graphics, Sprite, Text, type Texture } from "pixi.js";
import {
  type Facing,
  getMobTemplate,
  getNpcTemplate,
  type MobAiState,
  type MobSnapshot,
  MOVE_COOLDOWN_MS,
  MOVE_COOLDOWN_MS_DIAG,
  type NpcSnapshot,
  type PlayerSnapshot,
  RENDER_DELAY_MS,
  TILE_H,
  TILE_W,
} from "@vinyl/shared";
import { tileToScreen } from "./iso.js";
import { PlayerSpriteController, type LayerSlot } from "./player-sprite.js";

// Hero portrait used for every player (local + remotes). Single static frame —
// no facing variants, no walk cycle, no transparency. Preloaded on first use
// and shared across every player Sprite.
const HERO_URL = "/hero.png";
const HERO_TARGET_WIDTH = 48; // px on screen
let heroTexturePromise: Promise<Texture> | null = null;
function loadHeroTexture(): Promise<Texture> {
  heroTexturePromise ??= Assets.load<Texture>(HERO_URL);
  return heroTexturePromise;
}

const MOB_DEATH_FADE_MS = 800;
const MOB_DEATH_BLINK_HZ = 6;

function stepCooldown(dx: number, dy: number): number {
  return dx !== 0 && dy !== 0 ? MOVE_COOLDOWN_MS_DIAG : MOVE_COOLDOWN_MS;
}

function mobStepLerpMs(speedTilesPerSec: number, dx: number, dy: number): number {
  const baseMs = 1000 / speedTilesPerSec;
  return (dx !== 0 && dy !== 0 ? baseMs * Math.SQRT2 : baseMs) + RENDER_DELAY_MS;
}

interface RemoteView {
  container: Container;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  lerpStart: number;
  lerpDuration: number;
  facing: Facing;
  buffGlow: Graphics | null;
}

interface MobView {
  container: Container;
  templateId: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  lerpStart: number;
  lerpDuration: number;
  facing: Facing;
  hpFill: Graphics;
  hpBg: Graphics;
  speedTilesPerSec: number;
  lastHpPct: number;
  lastState: MobAiState | null;
  deathStartedAt: number | null;
  aggroDiamond: Graphics | null;
  aggroRangeTiles: number;
  aggroColor: number;
  // Debug overlay (F3): renders mob internals (state, losMissCount, next-swing
  // countdown) above the sprite. Lazy-created so non-debug play pays nothing.
  debugLabel: Text | null;
  isRanged: boolean;
  lastSwingAt: number;
  lastLosMissCount: number;
  lastState_dbg: MobAiState;
  // Latest slow multiplier from the snapshot (1.0 = no slow). Drives the
  // F3 overlay's effective-speed readout and the lerp duration scaling.
  lastSlowMult: number;
  // M17.2 — lazily-created "stunned" indicator (orbiting stars above the
  // head), shown while the mob is stunned by a Stunning Blow.
  stunIcon: Container | null;
  // M17.4 — Scholar's Eye label (Lv X · hp/maxHp), shown only to holders.
  scholarsLabel: Text | null;
}

interface LocalView {
  container: Container;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  lerpStart: number;
  lerpDuration: number;
  facing: Facing;
  initialized: boolean;
  buffGlow: Graphics | null;
}

interface BagView {
  container: Container;
  bagId: string;
  x: number;
  y: number;
}

interface NpcView {
  container: Container;
  npcId: string;
  templateId: string;
  x: number;
  y: number;
  speech?: { node: Container; timeoutId: number };
}

export interface GroundBagSnapshot {
  bagId: string;
  x: number;
  y: number;
  itemCount: number;
  expiresAt: number;
}

const LOCAL_COLOR = 0xd4af37;
const REMOTE_COLOR = 0x99c8ff;

// Local player uses the 3D-rendered sprite pipeline (idle/walking/punching ×
// 8 directions). Remotes and mobs keep the legacy static hero / debug shapes
// for now — they'll get the same treatment when M15 promotes this POC.
const LOCAL_CHARACTER = "human_m";
// Once the lerp ends we wait this grace period before swapping back to idle.
// Without it, chained moves (key held) briefly show idle frames between steps.
const LOCAL_IDLE_GRACE_MS = 80;

export class EntityLayer {
  readonly container = new Container();
  private readonly remotes = new Map<string, RemoteView>();
  private readonly mobs = new Map<string, MobView>();
  private readonly bags = new Map<string, BagView>();
  private readonly npcs = new Map<string, NpcView>();
  private local: { id: string; view: LocalView } | null = null;
  private localSpriteController: PlayerSpriteController | null = null;
  // Equipment layer sprites requested via setLocalLayer — stored so they
  // survive being set before the (async) controller finishes loading; applied
  // on ready. Keyed by layer slot (helmet, main_hand, …).
  private readonly desiredLayers: Partial<Record<LayerSlot, string | null>> = {};
  // Desired BODY variant requested via setLocalBody (torso armour bakes into a
  // body variant, not a layer). Stored so it survives being set before the
  // async controller load; applied on ready. null = no swap requested yet.
  private desiredBody: string | null = null;
  // Wall-clock at which the local player's animation should drop back to idle
  // (= end of current lerp + grace). null = no pending transition.
  private localIdleAt: number | null = null;
  private aggroOverlayVisible = false;
  private mobDebugVisible = false;
  private scholarsEye = false; // M17.4 — Scholar's Eye unlocked for local player
  private lastServerTime = 0;

  constructor() {
    this.container.label = "entities";
  }

  setAggroOverlayVisible(visible: boolean): void {
    this.aggroOverlayVisible = visible;
    for (const view of this.mobs.values()) {
      this.applyAggroVisibility(view);
    }
  }

  isAggroOverlayVisible(): boolean {
    return this.aggroOverlayVisible;
  }

  setMobDebugVisible(visible: boolean): void {
    this.mobDebugVisible = visible;
    for (const view of this.mobs.values()) {
      this.applyMobDebugVisibility(view);
    }
  }

  isMobDebugVisible(): boolean {
    return this.mobDebugVisible;
  }

  // M17.4 — Scholar's Eye: when on, every mob shows its exact level + HP.
  setScholarsEye(on: boolean): void {
    if (this.scholarsEye === on) return;
    this.scholarsEye = on;
    if (!on) {
      for (const view of this.mobs.values()) {
        if (view.scholarsLabel) view.scholarsLabel.visible = false;
      }
    }
    // When turning on, the next snapshot tick fills + shows the labels.
  }

  // Merge the per-tick mob-debug sidecar into the entity views. Called
  // from the `mobDebugSnapshot` server message — only fires while F3
  // is ON. Missing ids (mob despawned mid-tick) silently skipped.
  applyMobDebugSnapshot(mobs: readonly { id: string; losMissCount: number; swingAt: number }[]): void {
    for (const d of mobs) {
      const view = this.mobs.get(d.id);
      if (!view) continue;
      view.lastSwingAt = d.swingAt;
      view.lastLosMissCount = d.losMissCount;
    }
    if (this.mobDebugVisible) {
      for (const view of this.mobs.values()) this.refreshMobDebugText(view);
    }
  }

  private applyMobDebugVisibility(view: MobView): void {
    if (this.mobDebugVisible) {
      if (!view.debugLabel) {
        view.debugLabel = makeMobDebugLabel();
        view.container.addChild(view.debugLabel);
      }
      view.debugLabel.visible = true;
      this.refreshMobDebugText(view);
    } else if (view.debugLabel) {
      view.debugLabel.visible = false;
    }
  }

  private refreshMobDebugText(view: MobView): void {
    if (!view.debugLabel) return;
    const stateShort = mobStateShort(view.lastState_dbg);
    const swingMs = Math.max(0, view.lastSwingAt - this.lastServerTime);
    const swingTxt = swingMs <= 0 ? "rdy" : `${(swingMs / 1000).toFixed(1)}s`;
    const missTxt = view.isRanged ? ` m:${view.lastLosMissCount}` : "";
    // Effective move speed = base template speed × slow multiplier. The
    // "SLOW" marker makes it easy to spot a Frost-Shock'd mob at a glance.
    const effSpeed = view.speedTilesPerSec * view.lastSlowMult;
    const slowMark = view.lastSlowMult < 1 ? " SLOW" : "";
    const speedTxt = ` s:${effSpeed.toFixed(1)}t/s${slowMark}`;
    view.debugLabel.text = `${stateShort}${missTxt} ${swingTxt}${speedTxt}`;
  }

  private applyAggroVisibility(view: MobView): void {
    if (this.aggroOverlayVisible) {
      if (!view.aggroDiamond) {
        const g = makeAggroDiamond(view.aggroRangeTiles, view.aggroColor);
        view.container.addChildAt(g, 0);
        view.aggroDiamond = g;
      }
      view.aggroDiamond.visible = true;
    } else if (view.aggroDiamond) {
      view.aggroDiamond.visible = false;
    }
  }

  // Sets (or clears) a buff aura at the feet of a player sprite. color=null
  // removes the glow. Lazy-creates the Graphics on first use; the renderer
  // pulses alpha over time via the per-frame lerp tick.
  setPlayerBuffGlow(characterId: string, color: number | null): void {
    let target: LocalView | RemoteView | null = null;
    if (this.local && this.local.id === characterId) target = this.local.view;
    else target = this.remotes.get(characterId) ?? null;
    if (!target) return;
    if (color === null) {
      if (target.buffGlow) {
        target.container.removeChild(target.buffGlow);
        target.buffGlow.destroy();
        target.buffGlow = null;
      }
      return;
    }
    if (!target.buffGlow) {
      const g = new Graphics();
      // Behind sprite but above feet ring (index 1).
      target.container.addChildAt(g, 1);
      target.buffGlow = g;
    }
    target.buffGlow.clear();
    // Soft ellipse aura at the feet — wider + shorter than the sprite to read
    // as a magical glow rather than a hard ring.
    target.buffGlow
      .ellipse(0, 0, TILE_W * 0.4, TILE_H * 0.4)
      .fill({ color, alpha: 0.28 })
      .stroke({ color, alpha: 0.55, width: 1.5 });
  }

  clearRemotes(): void {
    for (const id of [...this.remotes.keys()]) this.removeRemote(id);
    for (const id of [...this.mobs.keys()]) this.removeMob(id);
    for (const id of [...this.bags.keys()]) this.removeBag(id);
    for (const id of [...this.npcs.keys()]) this.removeNpc(id);
  }

  // M5.1 — ingest the one-shot npcsInZone payload. NPCs are static, so this
  // is a wholesale replace of the current zone's NPCs (no lerp). Called on
  // initial zoneEnter and after every teleport.
  ingestNpcsInZone(snapshots: NpcSnapshot[]): void {
    const seen = new Set<string>();
    for (const s of snapshots) {
      seen.add(s.npcId);
      const template = getNpcTemplate(s.templateId);
      if (!template) continue;
      let view = this.npcs.get(s.npcId);
      if (!view) {
        const built = createNpcView(template.name);
        const { sx, sy } = tileToScreen(s.x, s.y);
        built.position.set(sx, sy);
        this.container.addChild(built);
        view = {
          container: built,
          npcId: s.npcId,
          templateId: s.templateId,
          x: s.x,
          y: s.y,
        };
        this.npcs.set(s.npcId, view);
      } else if (s.x !== view.x || s.y !== view.y) {
        const { sx, sy } = tileToScreen(s.x, s.y);
        view.container.position.set(sx, sy);
        view.x = s.x;
        view.y = s.y;
      }
    }
    for (const id of [...this.npcs.keys()]) {
      if (!seen.has(id)) this.removeNpc(id);
    }
  }

  // Returns {npcId, x, y} list for the click router to build its per-tile map.
  listNpcs(): { npcId: string; x: number; y: number }[] {
    const out: { npcId: string; x: number; y: number }[] = [];
    for (const v of this.npcs.values()) out.push({ npcId: v.npcId, x: v.x, y: v.y });
    return out;
  }

  getNpcPosition(npcId: string): { x: number; y: number } | null {
    const v = this.npcs.get(npcId);
    return v ? { x: v.x, y: v.y } : null;
  }

  private removeNpc(id: string): void {
    const view = this.npcs.get(id);
    if (!view) return;
    if (view.speech) window.clearTimeout(view.speech.timeoutId);
    this.container.removeChild(view.container);
    view.container.destroy({ children: true });
    this.npcs.delete(id);
  }

  // Floating speech bubble above the NPC. Used by the vendor goodbye flow;
  // could surface quest hints or ambient lines later.
  showNpcSpeech(npcId: string, text: string, durationMs = 5000): void {
    const view = this.npcs.get(npcId);
    if (!view) return;
    if (view.speech) {
      window.clearTimeout(view.speech.timeoutId);
      view.container.removeChild(view.speech.node);
      view.speech.node.destroy({ children: true });
    }
    const node = makeSpeechBubble(text);
    view.container.addChild(node);
    const timeoutId = window.setTimeout(() => {
      const v = this.npcs.get(npcId);
      if (!v || v.speech?.node !== node) return;
      v.container.removeChild(node);
      node.destroy({ children: true });
      v.speech = undefined;
    }, durationMs);
    view.speech = { node, timeoutId };
  }

  ingestBagsSnapshot(snapshots: GroundBagSnapshot[]): void {
    const seen = new Set<string>();
    for (const s of snapshots) {
      seen.add(s.bagId);
      let view = this.bags.get(s.bagId);
      if (!view) {
        const built = createBagView();
        const { sx, sy } = tileToScreen(s.x, s.y);
        built.position.set(sx, sy);
        this.container.addChild(built);
        view = { container: built, bagId: s.bagId, x: s.x, y: s.y };
        this.bags.set(s.bagId, view);
      } else if (s.x !== view.x || s.y !== view.y) {
        const { sx, sy } = tileToScreen(s.x, s.y);
        view.container.position.set(sx, sy);
        view.x = s.x;
        view.y = s.y;
      }
    }
    for (const id of [...this.bags.keys()]) {
      if (!seen.has(id)) this.removeBag(id);
    }
  }

  private removeBag(id: string): void {
    const view = this.bags.get(id);
    if (!view) return;
    this.container.removeChild(view.container);
    view.container.destroy({ children: true });
    this.bags.delete(id);
  }

  ingestMobSnapshot(snapshots: MobSnapshot[], now: number, serverTime: number): void {
    this.lastServerTime = serverTime;
    const seen = new Set<string>();
    for (const s of snapshots) {
      seen.add(s.id);
      const template = getMobTemplate(s.templateId);
      if (!template) continue;
      let view = this.mobs.get(s.id);
      if (!view) {
        const built = createMobView(template);
        const start = tileToScreen(s.x, s.y);
        built.container.position.set(start.sx, start.sy);
        this.container.addChild(built.container);
        view = {
          container: built.container,
          templateId: s.templateId,
          fromX: s.x,
          fromY: s.y,
          toX: s.x,
          toY: s.y,
          lerpStart: now,
          lerpDuration: 0,
          facing: s.facing,
          hpFill: built.hpFill,
          hpBg: built.hpBg,
          speedTilesPerSec: template.moveSpeedTilesPerSec,
          lastHpPct: -1,
          lastState: s.state,
          deathStartedAt: s.state === "dead" ? now - MOB_DEATH_FADE_MS : null,
          aggroDiamond: null,
          aggroRangeTiles: template.aggroRangeTiles,
          aggroColor: template.displayColor,
          debugLabel: null,
          isRanged: template.aiType === "rangedKite",
          // Debug fields default to 0 — populated by the
          // `mobDebugSnapshot` sidecar that fires only when F3 is on.
          lastSwingAt: 0,
          lastLosMissCount: 0,
          lastState_dbg: s.state,
          lastSlowMult: s.slowMult ?? 1,
          stunIcon: null,
          scholarsLabel: null,
        };
        if (s.state === "dead") {
          view.container.visible = false;
        }
        this.mobs.set(s.id, view);
        this.applyAggroVisibility(view);
        this.applyMobDebugVisibility(view);
      }
      const wasDead = view.lastState === "dead";
      const isDead = s.state === "dead";
      const respawning = wasDead && !isDead;
      if (respawning) {
        // Snap to the new tile (anchor) on respawn — without this the
        // lerp branch below would slide the sprite from its death position
        // to the anchor over a full move cooldown, which reads as a wrong
        // teleport.
        const { sx, sy } = tileToScreen(s.x, s.y);
        view.container.position.set(sx, sy);
        view.fromX = s.x;
        view.fromY = s.y;
        view.toX = s.x;
        view.toY = s.y;
        view.lerpStart = now;
        view.lerpDuration = 0;
      } else if (s.x !== view.toX || s.y !== view.toY) {
        const cur = lerpPos(view, now);
        const dx = s.x - view.toX;
        const dy = s.y - view.toY;
        view.fromX = cur.x;
        view.fromY = cur.y;
        view.toX = s.x;
        view.toY = s.y;
        view.lerpStart = now;
        // Scale lerp by any active slow so a Frost-Shocked mob crawls
        // visually at the same cadence the server actually moves it.
        const slow = s.slowMult ?? 1;
        const effSpeed = view.speedTilesPerSec * Math.max(0.01, slow);
        view.lerpDuration = mobStepLerpMs(effSpeed, dx, dy);
      }
      view.facing = s.facing;
      view.lastSlowMult = s.slowMult ?? 1;
      this.setMobStunned(view, (s.stunnedUntil ?? 0) > serverTime); // M17.2
      this.applyScholarsLabel(view, s); // M17.4
      if (view.lastHpPct !== s.hpPct) {
        renderMobHpBar(view, s.hpPct);
      }
      if (!wasDead && isDead) {
        view.deathStartedAt = now;
      } else if (respawning) {
        view.deathStartedAt = null;
        view.container.alpha = 1;
        view.container.visible = true;
      }
      view.lastState = s.state;
      view.lastState_dbg = s.state;
      // lastSwingAt / lastLosMissCount are merged from
      // `applyMobDebugSnapshot` (sidecar) — not from worldSnapshot
      // anymore. Still call refresh so the overlay text picks up the
      // freshly-updated state.
      if (this.mobDebugVisible) this.refreshMobDebugText(view);
    }
    for (const id of [...this.mobs.keys()]) {
      if (!seen.has(id)) this.removeMob(id);
    }
  }

  // M17.4 — Scholar's Eye label: Lv X · hp/maxHp, only for holders.
  private applyScholarsLabel(view: MobView, s: MobSnapshot): void {
    if (!this.scholarsEye || s.hp === undefined || s.maxHp === undefined) {
      if (view.scholarsLabel) view.scholarsLabel.visible = false;
      return;
    }
    if (!view.scholarsLabel) {
      view.scholarsLabel = makeScholarsLabel();
      view.container.addChild(view.scholarsLabel);
    }
    const tpl = getMobTemplate(s.templateId);
    const lvl = tpl
      ? tpl.level.min === tpl.level.max
        ? `Lv ${tpl.level.min}`
        : `Lv ${tpl.level.min}-${tpl.level.max}`
      : "";
    view.scholarsLabel.text = `${lvl} · ${Math.round(s.hp)}/${s.maxHp}`;
    view.scholarsLabel.visible = true;
  }

  // M17.2 — toggle the "stunned" star indicator above a mob.
  private setMobStunned(view: MobView, on: boolean): void {
    if (on && !view.stunIcon) {
      const icon = createStunStars();
      view.stunIcon = icon;
      view.container.addChild(icon);
    } else if (!on && view.stunIcon) {
      view.container.removeChild(view.stunIcon);
      view.stunIcon.destroy({ children: true });
      view.stunIcon = null;
    }
  }

  ensureLocal(id: string, name: string): LocalView {
    if (this.local && this.local.id === id) return this.local.view;
    if (this.local) {
      this.container.removeChild(this.local.view.container);
      this.local.view.container.destroy({ children: true });
    }
    if (this.localSpriteController) {
      this.localSpriteController.destroy();
      this.localSpriteController = null;
    }
    this.localIdleAt = null;
    const view = createLocalView(name);
    this.container.addChild(view.container);
    this.local = {
      id,
      view: {
        container: view.container,
        fromX: 0,
        fromY: 0,
        toX: 0,
        toY: 0,
        lerpStart: 0,
        lerpDuration: 0,
        facing: "S",
        initialized: false,
        buffGlow: null,
      },
    };
    // Async-load the 3D-rendered sprite controller (idle/walking/punching ×
    // 8 directions). Once ready, its container is inserted just before the
    // name label so the label stays on top regardless of any buff glow that
    // may have been added in the meantime.
    void PlayerSpriteController.create({ body: LOCAL_CHARACTER }).then((controller) => {
      if (!this.local || this.local.id !== id) {
        controller.destroy();
        return;
      }
      this.localSpriteController = controller;
      // Apply any body variant (torso armour) requested before this load.
      if (this.desiredBody && this.desiredBody !== LOCAL_CHARACTER) {
        void controller.setBody(this.desiredBody);
      }
      // Apply any layers requested via setLocalLayer (driven by equipped
      // slots) — may have been set before this async load completed.
      for (const slot of Object.keys(this.desiredLayers) as LayerSlot[]) {
        void controller.setLayer(slot, this.desiredLayers[slot] ?? null);
      }
      const c = this.local.view.container;
      const labelIdx = c.children.length - 1; // label is always last
      c.addChildAt(controller.container, Math.max(1, labelIdx));
    });
    return this.local.view;
  }

  // Resolves a targetId to a (dx, dy) game-space facing vector relative to the
  // local player. Used by all attack one-shots to make the character face the
  // target tile rather than keep its last movement facing.
  private resolveTargetFacing(targetId: string | undefined): { dx: number; dy: number } | null {
    if (!targetId || !this.local) return null;
    const localView = this.local.view;
    // Prefer the authoritative target tile (toX/toY) over lerp position —
    // the swing should face where the target really is, not its tween.
    const mob = this.mobs.get(targetId);
    if (mob) return { dx: mob.toX - localView.toX, dy: mob.toY - localView.toY };
    const remote = this.remotes.get(targetId);
    if (remote) return { dx: remote.toX - localView.toX, dy: remote.toY - localView.toY };
    return null;
  }

  triggerLocalPunch(targetId?: string): void {
    if (!this.localSpriteController) return;
    const facing = this.resolveTargetFacing(targetId);
    this.localSpriteController.triggerPunch(facing?.dx, facing?.dy);
  }

  // 1H weapon swing — fired when the local player attacks while a 1H weapon
  // is in RightHand. Mirrors triggerLocalPunch's targeting logic.
  triggerLocalAttack1h(targetId?: string): void {
    if (!this.localSpriteController) return;
    const facing = this.resolveTargetFacing(targetId);
    this.localSpriteController.triggerAttack1h(facing?.dx, facing?.dy);
  }

  // 2H weapon swing — fired when the local player attacks while a 2H weapon
  // occupies both RightHand and LeftHand.
  triggerLocalAttack2h(targetId?: string): void {
    if (!this.localSpriteController) return;
    const facing = this.resolveTargetFacing(targetId);
    this.localSpriteController.triggerAttack2h(facing?.dx, facing?.dy);
  }

  // Show/hide the local player's helmet layer. spriteId === null clears it.
  // Stored as desiredHelmet so a call before the controller finishes loading
  // is applied once it's ready (see the create().then above).
  setLocalLayer(slot: LayerSlot, spriteId: string | null): void {
    this.desiredLayers[slot] = spriteId;
    void this.localSpriteController?.setLayer(slot, spriteId);
  }

  // Swap the local player's BODY variant (torso armour is baked into a body
  // variant for correct occlusion over the arms). Stored as desiredBody so a
  // call before the controller finishes loading is applied on ready.
  setLocalBody(bodyName: string): void {
    this.desiredBody = bodyName;
    void this.localSpriteController?.setBody(bodyName);
  }

  // Fired when the local player sends a castSpell. targetTile pivots the
  // character toward where the spell is going (mob tile, ally tile, AOE tile);
  // omit for self-cast (Alt+N or self-targeted spells) — no rotation.
  triggerLocalCast(targetTile?: { x: number; y: number }): void {
    if (!this.localSpriteController) return;
    if (!targetTile || !this.local) {
      this.localSpriteController.triggerCast();
      return;
    }
    const localView = this.local.view;
    this.localSpriteController.triggerCast(
      targetTile.x - localView.toX,
      targetTile.y - localView.toY,
    );
  }

  setLocalPosition(tx: number, ty: number, facing: Facing, snap = false): void {
    if (!this.local) return;
    const view = this.local.view;
    const now = performance.now();
    if (!view.initialized || snap) {
      view.fromX = tx;
      view.fromY = ty;
      view.toX = tx;
      view.toY = ty;
      view.lerpStart = now;
      view.lerpDuration = 0;
      view.initialized = true;
      const { sx, sy } = tileToScreen(tx, ty);
      view.container.position.set(sx, sy);
      // Spawn/teleport: drop to idle immediately, cancel any pending grace.
      this.localSpriteController?.setIdle();
      this.localIdleAt = null;
    } else if (tx !== view.toX || ty !== view.toY) {
      const cur = lerpPos(view, now);
      const dx = tx - view.toX;
      const dy = ty - view.toY;
      // Base cooldown assumes the sprite starts AT the old target tile
      // and traverses exactly 1 cardinal (or 1 diagonal) step. When we
      // chain a move while the previous lerp is still in flight, the
      // sprite's current visual position (cur.x, cur.y) is BEHIND the
      // old target — so the true distance to the new target is more
      // than 1 step. Scale duration by (trueDist / standardDist) to
      // preserve constant tile-per-second speed across chained moves;
      // otherwise the sprite snaps visually (if we kept stepCooldown
      // as-is the duration is too short for the real distance, causing
      // the perceived "jolt forward").
      const baseDuration = stepCooldown(dx, dy);
      const isDiag = dx !== 0 && dy !== 0;
      const standardDist = isDiag ? Math.SQRT2 : 1;
      const trueDist = Math.hypot(tx - cur.x, ty - cur.y);
      view.fromX = cur.x;
      view.fromY = cur.y;
      view.toX = tx;
      view.toY = ty;
      view.lerpStart = now;
      view.lerpDuration = (trueDist / standardDist) * baseDuration;
      // Sprite: switch to walking with the right 8-way facing, schedule
      // the idle handoff for after this lerp finishes (+ grace so chained
      // moves don't flash an idle frame between steps).
      this.localSpriteController?.setMoving(
        Math.sign(dx) as -1 | 0 | 1,
        Math.sign(dy) as -1 | 0 | 1,
      );
      this.localIdleAt = now + view.lerpDuration + LOCAL_IDLE_GRACE_MS;
    }
    view.facing = facing;
  }

  ingestSnapshot(snapshots: PlayerSnapshot[], localId: string, now: number): void {
    const seen = new Set<string>();
    for (const s of snapshots) {
      if (s.id === localId) continue;
      seen.add(s.id);
      let view = this.remotes.get(s.id);
      if (!view) {
        const v = createView(s.name, false);
        const start = tileToScreen(s.x, s.y);
        v.container.position.set(start.sx, start.sy);
        this.container.addChild(v.container);
        view = {
          container: v.container,
          fromX: s.x,
          fromY: s.y,
          toX: s.x,
          toY: s.y,
          lerpStart: now,
          lerpDuration: 0,
          facing: s.facing,
          buffGlow: null,
        };
        this.remotes.set(s.id, view);
        // Fall through so the disconnected alpha lands on the first frame
        // a remote appears as a ghost (e.g. zone entry while another player
        // is mid-grace).
      }
      if (s.x !== view.toX || s.y !== view.toY) {
        const cur = lerpPos(view, now);
        const dx = s.x - view.toX;
        const dy = s.y - view.toY;
        view.fromX = cur.x;
        view.fromY = cur.y;
        view.toX = s.x;
        view.toY = s.y;
        view.lerpStart = now;
        view.lerpDuration = stepCooldown(dx, dy) + RENDER_DELAY_MS;
      }
      view.facing = s.facing;
      // M6.1 — fade the sprite while the player is in reconnect grace (their
      // WS is gone but the runtime stays in zone, mob keeps fighting them).
      view.container.alpha = s.disconnected ? 0.45 : 1;
    }
    for (const id of [...this.remotes.keys()]) {
      if (!seen.has(id)) this.removeRemote(id);
    }
  }

  updatePositions(now: number): void {
    if (this.local) {
      const cur = lerpPos(this.local.view, now);
      const { sx, sy } = tileToScreen(cur.x, cur.y);
      this.local.view.container.position.set(sx, sy);
      if (this.localSpriteController) {
        if (this.localIdleAt !== null && now >= this.localIdleAt) {
          this.localSpriteController.setIdle();
          this.localIdleAt = null;
        }
        this.localSpriteController.update(now);
      }
    }
    for (const view of this.remotes.values()) {
      const cur = lerpPos(view, now);
      const { sx, sy } = tileToScreen(cur.x, cur.y);
      view.container.position.set(sx, sy);
    }
    for (const view of this.mobs.values()) {
      const cur = lerpPos(view, now);
      const { sx, sy } = tileToScreen(cur.x, cur.y);
      view.container.position.set(sx, sy);
      if (view.deathStartedAt !== null) {
        const elapsed = now - view.deathStartedAt;
        if (elapsed >= MOB_DEATH_FADE_MS) {
          view.container.alpha = 0;
          view.container.visible = false;
        } else {
          const t = elapsed / MOB_DEATH_FADE_MS;
          const blink = 0.5 + 0.5 * Math.cos((elapsed / 1000) * MOB_DEATH_BLINK_HZ * Math.PI * 2);
          view.container.alpha = Math.max(0, blink * (1 - t));
        }
      }
    }
  }

  getRemoteView(id: string): RemoteView | undefined {
    return this.remotes.get(id);
  }

  getLocalScreenPos(): { sx: number; sy: number } | null {
    if (!this.local) return null;
    return { sx: this.local.view.container.x, sy: this.local.view.container.y };
  }

  getScreenPos(id: string): { sx: number; sy: number } | null {
    if (this.local && this.local.id === id) return this.getLocalScreenPos();
    const v = this.remotes.get(id);
    if (v) return { sx: v.container.x, sy: v.container.y };
    const m = this.mobs.get(id);
    if (m) return { sx: m.container.x, sy: m.container.y };
    return null;
  }

  private removeRemote(id: string): void {
    const view = this.remotes.get(id);
    if (!view) return;
    this.container.removeChild(view.container);
    view.container.destroy({ children: true });
    this.remotes.delete(id);
  }

  private removeMob(id: string): void {
    const view = this.mobs.get(id);
    if (!view) return;
    this.container.removeChild(view.container);
    view.container.destroy({ children: true });
    this.mobs.delete(id);
  }
}

interface LerpFrame {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  lerpStart: number;
  lerpDuration: number;
}

function lerpPos(view: LerpFrame, now: number): { x: number; y: number } {
  if (view.lerpDuration <= 0) return { x: view.toX, y: view.toY };
  const elapsed = now - view.lerpStart;
  if (elapsed >= view.lerpDuration) return { x: view.toX, y: view.toY };
  const t = Math.max(0, elapsed / view.lerpDuration);
  return {
    x: view.fromX + (view.toX - view.fromX) * t,
    y: view.fromY + (view.toY - view.fromY) * t,
  };
}

function createView(name: string, isLocal: boolean): { container: Container } {
  const c = new Container();
  c.label = `entity:${name}`;

  // Placeholder ring rendered while the hero texture is still loading; also
  // stays as a subtle "feet shadow" once the sprite is in place.
  const feetRing = new Graphics()
    .ellipse(0, 0, TILE_W * 0.22, TILE_H * 0.22)
    .fill({ color: 0x000000, alpha: 0.3 })
    .stroke({ color: isLocal ? LOCAL_COLOR : REMOTE_COLOR, alpha: 0.7, width: 1.5 });

  const sprite = new Sprite();
  sprite.anchor.set(0.5, 0.92); // feet roughly at the tile origin
  sprite.alpha = 0; // hide until the texture lands

  const label = new Text({
    text: name,
    style: {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 11,
      fill: isLocal ? LOCAL_COLOR : REMOTE_COLOR,
      stroke: { color: 0x000000, width: 3 },
      fontWeight: "600",
    },
  });
  label.anchor.set(0.5, 1);
  label.position.set(0, -56); // raised so it sits above the sprite head

  c.addChild(feetRing, sprite, label);

  void loadHeroTexture().then((texture) => {
    if (sprite.destroyed) return;
    sprite.texture = texture;
    const aspect = texture.height / texture.width;
    sprite.width = HERO_TARGET_WIDTH;
    sprite.height = HERO_TARGET_WIDTH * aspect;
    sprite.alpha = 1;
  });

  return { container: c };
}

// Local-player view: same feet ring + label as createView, but no static
// hero sprite. PlayerSpriteController inserts its own Container at index 1
// once the 3D-rendered textures finish loading.
function createLocalView(name: string): { container: Container } {
  const c = new Container();
  c.label = `entity:${name}`;

  const feetRing = new Graphics()
    .ellipse(0, 0, TILE_W * 0.22, TILE_H * 0.22)
    .fill({ color: 0x000000, alpha: 0.3 })
    .stroke({ color: LOCAL_COLOR, alpha: 0.7, width: 1.5 });

  const label = new Text({
    text: name,
    style: {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 11,
      fill: LOCAL_COLOR,
      stroke: { color: 0x000000, width: 3 },
      fontWeight: "600",
    },
  });
  label.anchor.set(0.5, 1);
  // Sits just above the 3D-rendered character head. With TARGET_HEIGHT=130
  // and FEET_ANCHOR_Y=0.70, the head lands around y=-70 in container coords,
  // so -65 keeps the label tight to the head without overlapping.
  label.position.set(0, -65);

  c.addChild(feetRing, label);

  return { container: c };
}

const MOB_HP_BAR_W = 22;
const MOB_HP_BAR_H = 3;

function createMobView(template: {
  id: string;
  displayTag: string;
  displayColor: number;
  spritePath?: string;
}): { container: Container; hpFill: Graphics; hpBg: Graphics } {
  const c = new Container();
  c.label = `mob:${template.id}`;
  const body = new Graphics()
    .circle(0, 0, 10)
    .fill({ color: template.displayColor })
    .stroke({ color: 0x000000, alpha: 0.55, width: 1.5 });
  const tag = new Text({
    text: template.displayTag,
    style: {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 11,
      fill: 0xffffff,
      stroke: { color: 0x000000, width: 3 },
      fontWeight: "700",
    },
  });
  tag.anchor.set(0.5, 0.5);
  tag.position.set(0, 0);
  const hpBg = new Graphics();
  const hpFill = new Graphics();
  hpBg.position.set(-MOB_HP_BAR_W / 2, -18);
  hpFill.position.set(-MOB_HP_BAR_W / 2, -18);
  c.addChild(body, tag, hpBg, hpFill);
  // M7.2 — when a sprite path is set, asynchronously load it and hide the
  // placeholder body+tag once the texture lands. On load failure we keep
  // the placeholder visible silently (no console spam — the admin will see
  // the missing sprite visually and check their path).
  if (template.spritePath) {
    const url = `/assets/${template.spritePath}`;
    void Assets.load<Texture>(url)
      .then((tex) => {
        if (!tex || c.destroyed) return;
        const sprite = new Sprite(tex);
        sprite.anchor.set(0.5, 0.5);
        // Reasonable default size: 32 px tall (matches Kenney tile size).
        // Aspect ratio preserved.
        const targetH = 32;
        const scale = targetH / tex.height;
        sprite.scale.set(scale);
        // Insert behind the HP bar, in front of the placeholder body.
        c.addChildAt(sprite, 1);
        body.visible = false;
        tag.visible = false;
      })
      .catch(() => {
        // Asset load failed — keep placeholder. Silent on purpose.
      });
  }
  return { container: c, hpFill, hpBg };
}

// M17.2 — three little yellow 4-point stars above a stunned mob's head.
function createStunStars(): Container {
  const c = new Container();
  c.label = "stun-stars";
  const star = (cx: number, cy: number, r: number): Graphics => {
    const g = new Graphics();
    const pts: number[] = [];
    for (let i = 0; i < 8; i++) {
      const ang = (Math.PI / 4) * i - Math.PI / 2;
      const rad = i % 2 === 0 ? r : r * 0.42;
      pts.push(cx + Math.cos(ang) * rad, cy + Math.sin(ang) * rad);
    }
    g.poly(pts).fill({ color: 0xffe14d }).stroke({ color: 0x8a6a00, width: 0.5 });
    return g;
  };
  c.addChild(star(-9, -30, 3.2), star(0, -34, 4), star(9, -30, 3.2));
  return c;
}

function renderMobHpBar(view: MobView, hpPct: number): void {
  view.lastHpPct = hpPct;
  view.hpBg.clear();
  view.hpFill.clear();
  if (hpPct >= 0.999) return;
  view.hpBg
    .rect(0, 0, MOB_HP_BAR_W, MOB_HP_BAR_H)
    .fill({ color: 0x000000, alpha: 0.55 });
  const w = Math.max(0, Math.min(1, hpPct)) * MOB_HP_BAR_W;
  view.hpFill.rect(0, 0, w, MOB_HP_BAR_H).fill({ color: 0xc02020 });
}

function createBagView(): Container {
  const c = new Container();
  c.label = "bag";
  // T4C-ish "bag pile": a brown ellipse base + a slight gold highlight at the
  // top so it's visible on dark tiles. Not animated.
  const shadow = new Graphics()
    .ellipse(0, 4, 12, 5)
    .fill({ color: 0x000000, alpha: 0.45 });
  const pile = new Graphics()
    .ellipse(0, 0, 11, 8)
    .fill({ color: 0x6b4220 })
    .stroke({ color: 0x2c1a08, width: 1.5 });
  const tie = new Graphics()
    .rect(-3, -7, 6, 3)
    .fill({ color: 0x8a5a2a })
    .stroke({ color: 0x2c1a08, width: 1 });
  const sparkle = new Graphics()
    .circle(-3, -4, 1.5)
    .fill({ color: 0xf0d56e, alpha: 0.85 });
  c.addChild(shadow, pile, tie, sparkle);
  return c;
}

// M5.1 — placeholder vendor sprite. Soft tan body + golden name label so the
// NPC reads as friendly and is easy to find. Static — no movement, no HP bar.
function createNpcView(name: string): Container {
  const c = new Container();
  c.label = `npc:${name}`;
  const feetRing = new Graphics()
    .ellipse(0, 0, TILE_W * 0.22, TILE_H * 0.22)
    .fill({ color: 0x000000, alpha: 0.3 })
    .stroke({ color: 0xe8b94a, alpha: 0.85, width: 1.5 });
  const body = new Graphics()
    .circle(0, -12, 11)
    .fill({ color: 0xc8a26a })
    .stroke({ color: 0x5a3b14, width: 1.5 });
  const label = new Text({
    text: name,
    style: {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 11,
      fill: 0xf5d27a,
      stroke: { color: 0x000000, width: 3 },
      fontWeight: "700",
    },
  });
  label.anchor.set(0.5, 1);
  label.position.set(0, -34);
  c.addChild(feetRing, body, label);
  return c;
}

// Speech bubble above an NPC. Auto-sized to the text; framed in the same
// gold/navy palette as the vendor modal so it reads as "the NPC speaking".
function makeSpeechBubble(text: string): Container {
  const c = new Container();
  c.label = "npc-speech";
  const body = new Text({
    text,
    style: {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 12,
      fill: 0xfaf2d8,
      fontWeight: "500",
      wordWrap: true,
      wordWrapWidth: 220,
      align: "center",
    },
  });
  body.anchor.set(0.5, 1);
  const padX = 10;
  const padY = 6;
  const w = body.width + padX * 2;
  const h = body.height + padY * 2;
  const tailH = 5;
  const bg = new Graphics()
    .roundRect(-w / 2, -h - tailH, w, h, 6)
    .fill({ color: 0x141833, alpha: 0.94 })
    .stroke({ color: 0xe8b94a, alpha: 0.75, width: 1.5 })
    .moveTo(-4, -tailH)
    .lineTo(0, 0)
    .lineTo(4, -tailH)
    .closePath()
    .fill({ color: 0x141833, alpha: 0.94 });
  body.position.set(0, -tailH - padY);
  c.addChild(bg, body);
  // Float above the NPC's name label (which sits at y = -34).
  c.position.set(0, -42);
  return c;
}

function makeMobDebugLabel(): Text {
  const t = new Text({
    text: "",
    style: {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 10,
      fill: 0xfff0a0,
      stroke: { color: 0x000000, width: 3 },
      fontWeight: "600",
    },
  });
  t.anchor.set(0.5, 1);
  t.position.set(0, -34); // above the HP bar
  t.label = "mob-debug";
  return t;
}

function makeScholarsLabel(): Text {
  const t = new Text({
    text: "",
    style: {
      fontFamily: "Inter, system-ui, sans-serif",
      fontSize: 10,
      fill: 0x9fd0ff, // scholarly blue
      stroke: { color: 0x000000, width: 3 },
      fontWeight: "600",
    },
  });
  t.anchor.set(0.5, 1);
  t.position.set(0, -22); // just above the HP bar, below the stun stars
  t.label = "mob-scholars";
  return t;
}

function mobStateShort(state: MobAiState): string {
  switch (state) {
    case "idle": return "idle";
    case "patrolling": return "patr";
    case "chasing": return "chas";
    case "attacking": return "attk";
    case "leashing": return "leash";
    case "dead": return "dead";
  }
}

function makeAggroDiamond(rangeTiles: number, color: number): Graphics {
  // The aggro check is Chebyshev: a "ball of radius R" in tile space is a square
  // with corners at (±R, ±R). In iso projection that square's corners land at
  // (0, ±R*TILE_H) and (±R*TILE_W, 0) relative to the mob centre.
  const halfW = rangeTiles * TILE_W;
  const halfH = rangeTiles * TILE_H;
  const g = new Graphics();
  g.label = "aggro-diamond";
  g.poly([0, -halfH, halfW, 0, 0, halfH, -halfW, 0])
    .fill({ color, alpha: 0.1 })
    .stroke({ color, alpha: 0.7, width: 1.5 });
  return g;
}
